import React from 'react';
import type { ScenarioEditPresenceRecord } from '../../data/types';
import { useI18n } from '../../i18n/I18nContext';
import './ScenarioPresenceBanner.css';

interface ScenarioPresenceBannerProps {
  presence: ScenarioEditPresenceRecord | null;
  loading?: boolean;
  canReleaseEditing?: boolean;
  releaseEditingLabel?: string | null;
  onClaimEditing?: () => void;
  onReleaseEditing?: () => void;
  onTakeOver?: () => void;
  onRequestAccess?: () => void;
  onApproveRequest?: () => void;
  onDenyRequest?: () => void;
}

export default function ScenarioPresenceBanner({
  presence,
  loading = false,
  canReleaseEditing = false,
  releaseEditingLabel,
  onClaimEditing,
  onReleaseEditing,
  onTakeOver,
  onRequestAccess,
  onApproveRequest,
  onDenyRequest,
}: ScenarioPresenceBannerProps) {
  const { t } = useI18n();
  const effectiveReleaseEditingLabel = releaseEditingLabel || t('presence.releaseEditing');

  if (!presence) {
    return null;
  }

  const isReadOnly = Boolean(presence && !presence.canEdit);
  const isAvailableToClaim =
    Boolean(presence) &&
    !presence?.lockActive &&
    !presence?.isEditor &&
    !presence?.requestPendingForCurrentUser &&
    !presence?.requestDeniedForCurrentUser;
  const shouldShowCompactEditor =
    Boolean(presence?.isEditor) &&
    !isReadOnly &&
    !presence?.canApproveRequest &&
    !presence?.requestPendingForCurrentUser;
  const className = [
    'scenario-presence-banner',
    presence?.isEditor ? 'scenario-presence-banner--editor' : '',
    shouldShowCompactEditor ? 'scenario-presence-banner--editor-compact' : '',
    isAvailableToClaim ? 'scenario-presence-banner--available' : '',
    isReadOnly ? 'scenario-presence-banner--readonly' : '',
  ].filter(Boolean).join(' ');

  let title = t('presence.editingTitle');
  if (isAvailableToClaim) {
    title = t('presence.availableTitle');
  } else if (presence?.requestDeniedForCurrentUser) {
    title = t('presence.requestDeniedTitle');
  } else if (isReadOnly) {
    title = t('presence.readOnlyTitle');
  } else if (presence?.requestPendingForCurrentUser) {
    title = t('presence.requestPendingTitle');
  }

  return (
    <div className={className}>
      {shouldShowCompactEditor ? (
        <>
          <div className="scenario-presence-banner__compact-message">
            {presence?.releasePending && presence.returnTarget
              ? t('presence.releasePendingEditor', { name: presence.returnTarget.displayName })
              : t('presence.editingTitle')}
          </div>
          {canReleaseEditing ? (
            <div className="scenario-presence-banner__actions scenario-presence-banner__actions--compact">
              <button
                type="button"
                className="scenario-presence-banner__button scenario-presence-banner__button--secondary"
                onClick={onReleaseEditing}
                disabled={loading}
              >
                {loading && presence?.releasePending
                  ? t('presence.releasePendingButton')
                  : effectiveReleaseEditingLabel}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="scenario-presence-banner__content">
            <div className="scenario-presence-banner__eyebrow">
              {isAvailableToClaim
                ? t('presence.availableBadge')
                : presence?.isEditor
                  ? t('presence.editingBadge')
                  : t('presence.readOnlyBadge')}
            </div>
            <div className="scenario-presence-banner__title">{title}</div>
            {isAvailableToClaim ? (
              <div className="scenario-presence-banner__message">{t('presence.availableMessage')}</div>
            ) : presence?.requestDeniedForCurrentUser ? (
              <div className="scenario-presence-banner__message">{t('presence.requestDeniedMessage')}</div>
            ) : presence?.editor ? (
              <div className="scenario-presence-banner__message">
                {presence.isEditor
                  ? t('presence.editingMessage')
                  : t('presence.readOnlyMessage', { name: presence.editor.displayName })}
              </div>
            ) : (
              <div className="scenario-presence-banner__message">{t('presence.noEditorMessage')}</div>
            )}
            {presence?.requestPendingForCurrentUser ? (
              <div className="scenario-presence-banner__note">{t('presence.requestPendingNote')}</div>
            ) : null}
            {presence?.releasePendingForCurrentUser ? (
              <div className="scenario-presence-banner__note">
                {t('presence.releasePendingForYou', { name: presence.editor?.displayName ?? t('presence.someone') })}
              </div>
            ) : null}
            {presence?.requestDeniedForCurrentUser && presence.requestDeniedReason ? (
              <div className="scenario-presence-banner__note">
                {t('presence.requestDeniedReason', { reason: presence.requestDeniedReason })}
              </div>
            ) : null}
            {presence?.canApproveRequest && presence.request ? (
              <div className="scenario-presence-banner__note">
                {t('presence.requestReceived', { name: presence.request.user.displayName })}
              </div>
            ) : null}
          </div>
          <div className="scenario-presence-banner__actions">
            {isAvailableToClaim ? (
              <button type="button" className="scenario-presence-banner__button" onClick={onClaimEditing} disabled={loading}>
                {t('presence.claimEditing')}
              </button>
            ) : null}
            {canReleaseEditing ? (
              <button
                type="button"
                className="scenario-presence-banner__button scenario-presence-banner__button--secondary"
                onClick={onReleaseEditing}
                disabled={loading}
              >
                {effectiveReleaseEditingLabel}
              </button>
            ) : null}
            {presence?.canRequestAccess ? (
              <button type="button" className="scenario-presence-banner__button" onClick={onRequestAccess} disabled={loading}>
                {t('presence.requestAccess')}
              </button>
            ) : null}
            {presence?.canTakeOver && !presence?.releasePending ? (
              <button type="button" className="scenario-presence-banner__button scenario-presence-banner__button--danger" onClick={onTakeOver} disabled={loading}>
                {t('presence.takeOver')}
              </button>
            ) : null}
            {presence?.canApproveRequest && presence.request ? (
              <>
                <button type="button" className="scenario-presence-banner__button" onClick={onApproveRequest} disabled={loading}>
                  {t('presence.approveRequest')}
                </button>
                <button type="button" className="scenario-presence-banner__button scenario-presence-banner__button--secondary" onClick={onDenyRequest} disabled={loading}>
                  {t('presence.denyRequest')}
                </button>
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
